import { useCallback } from "react";

import { langCodeToJapanese } from "@/lib/chillnn/lang-code";

import { useCustomMutation } from "@/hooks/use-graphql-client";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { viewModelToDayTripPlanInputConverter, viewModelToPlanCustomMailSettingInputConverter } from "./composition";
import { DayTripPlanI18nUpdateMutation } from "./mutation";

import type { ProductDayTripPlanI18nFormModel } from "../../../type";

type Props = {
	onResetViewModel: () => void;
};

export const useDayTripPlanI18nEditMutation = ({ onResetViewModel }: Props) => {
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();

	const { mutationFunc } = useCustomMutation(DayTripPlanI18nUpdateMutation);

	const onUpdate = useCallback(
		async (
			planI18nViewModel: ProductDayTripPlanI18nFormModel["ViewModel"], //
		) => {
			showLoadingScreenCallback();

			const { lang } = planI18nViewModel;
			const langCodeText = langCodeToJapanese(lang);

			const input = viewModelToDayTripPlanInputConverter({ viewModel: planI18nViewModel });
			const planCustomMailSettingInput = viewModelToPlanCustomMailSettingInputConverter({
				viewModel: planI18nViewModel,
			});

			try {
				// プランI18n更新
				const { errors: planErrors } = await mutationFunc({
					variables: {
						input,
						dayTripPlanCustomMailSettingI18nInput: planCustomMailSettingInput,
					},
				});

				if (planErrors) {
					return;
				}

				notifyUserEventInfo({
					variant: "default",
					title: `日帰りプラン（${langCodeText}）を更新しました`,
				});

				onResetViewModel();
			} finally {
				hideLoadingScreenCallback();
			}
		},
		[hideLoadingScreenCallback, mutationFunc, onResetViewModel, showLoadingScreenCallback],
	);

	return { onUpdate };
};
